import {useCallback, useEffect, useRef, useState} from "react";

/**
 * Talks to the /api routes that guard the private section.
 *
 * No secret ever lives here: the code is posted once and verified on the
 * server, the session is an HttpOnly cookie this code cannot read, and the
 * content only arrives after that cookie checks out.
 */
export function usePrivateAuth() {
  // "checking" | "locked" | "unlocked"
  const [status, setStatus] = useState("checking");
  const [content, setContent] = useState(null);
  const [collections, setCollections] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Both are behind the same cookie check, so fetch them together.
  const loadPrivateData = useCallback(async () => {
    const [contentResponse, documentsResponse] = await Promise.all([
      fetch("/api/private", {credentials: "same-origin"}),
      fetch("/api/learning", {credentials: "same-origin"})
    ]);

    if (!contentResponse.ok) throw new Error("Could not load private content");
    const contentData = await contentResponse.json();

    // A missing or broken document index should not hide the rest of the page.
    let collectionList = [];
    let syncing = false;
    if (documentsResponse.ok) {
      const documentsData = await documentsResponse.json().catch(() => ({}));
      collectionList = documentsData.collections || [];
      syncing = Boolean(documentsData.syncing);
    }

    return {content: contentData.content, collections: collectionList, syncing};
  }, []);

  // Restore an existing session on mount so a returning visit — on any device
  // that still has the cookie — skips the code entirely.
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const response = await fetch("/api/session", {
          credentials: "same-origin"
        });
        if (!response.ok) throw new Error("session check failed");

        const {authenticated} = await response.json();
        if (cancelled || !isMounted.current) return;

        if (!authenticated) {
          setStatus("locked");
          return;
        }

        const data = await loadPrivateData();
        if (cancelled || !isMounted.current) return;

        setContent(data.content);
        setCollections(data.collections);
        setIsSyncing(data.syncing);
        setStatus("unlocked");
      } catch (err) {
        if (cancelled || !isMounted.current) return;
        setStatus("locked");
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [loadPrivateData]);

  const login = useCallback(
    async code => {
      setIsSubmitting(true);
      setError(null);

      try {
        const response = await fetch("/api/login", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          credentials: "same-origin",
          body: JSON.stringify({code})
        });

        const loginResult = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (isMounted.current) {
            setError(loginResult.error || "Login failed. Please try again.");
          }
          return false;
        }

        const data = await loadPrivateData();
        if (!isMounted.current) return true;

        setContent(data.content);
        setCollections(data.collections);
        setIsSyncing(data.syncing);
        setStatus("unlocked");
        return true;
      } catch (err) {
        if (isMounted.current) {
          setError("Could not reach the server. Please try again.");
        }
        return false;
      } finally {
        if (isMounted.current) setIsSubmitting(false);
      }
    },
    [loadPrivateData]
  );

  const logout = useCallback(async () => {
    try {
      await fetch("/api/logout", {
        method: "POST",
        credentials: "same-origin"
      });
    } catch (err) {
      // Even if the request fails, drop the content locally.
    }
    if (!isMounted.current) return;
    setContent(null);
    setCollections([]);
    setIsSyncing(false);
    setError(null);
    setStatus("locked");
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    status,
    content,
    collections,
    isSyncing,
    error,
    isSubmitting,
    login,
    logout,
    clearError
  };
}
